// src/core/api/apiService.ts
import { axiosInstance } from './axiosConfig';
import { ApiResponse } from '../types';
import axios from 'axios';

class ApiService {
  async request<T = any>({
    url,
    method = 'get',
    data = {},
    params = {},
  }: {
    url: string;
    method?: 'get' | 'post' | 'patch' | 'put' | 'delete';
    data?: any;
    params?: any;
  }): Promise<T> {
    try {
      const response = await axiosInstance.request<ApiResponse<T>>({
        url,
        method,
        data: method !== 'get' ? data : undefined,
        params: method === 'get' ? params : undefined,
      });
      // Log response for debugging (only in dev mode or for specific endpoints)
      const runtimeFlags = (globalThis as typeof globalThis & { __SODOR_DEBUG_FLAGS__?: { dev?: boolean } }).__SODOR_DEBUG_FLAGS__ ?? {};
      if (runtimeFlags.dev || url.includes('remittances/find')) {
        console.log('📡 API Response received:', {
          url,
          status: response.status,
          hasData: !!response.data,
          dataKeys: response.data ? Object.keys(response.data) : [],
          dataType: typeof response.data,
          dataPreview: response.data ? JSON.stringify(response.data).substring(0, 200) : 'null',
        });
      }
      
      // Handle different response structures
      if (response?.data) {
        const responseData = response.data as any;
        
        // Check if response contains an error message (server returned 200 but with error)
        // Check multiple possible error locations
        const errorMessage = responseData?.message || 
                            responseData?.error || 
                            responseData?.errors ||
                            (typeof responseData === 'string' && responseData.includes('Undefined variable') ? responseData : null);
        
        if (errorMessage && (
          String(errorMessage).includes('Undefined variable') ||
          String(errorMessage).includes('undefined variable') ||
          (String(errorMessage).includes('خطا') && !String(errorMessage).includes('خطا در') && !String(errorMessage).includes('خطای'))
        )) {
          console.error('⚠️ Server error detected in response (200 OK but contains error):', {
            message: errorMessage,
            fullResponse: responseData,
            responseStatus: response.status,
          });
          throw new Error(String(errorMessage) || 'خطای سرور: متغیر تعریف نشده');
        }
        
        // For remittances/find endpoint, handle response structure carefully
        if (url.includes('remittances/find')) {
          // Log the actual response structure for debugging
          console.log('🔍 remittances/find response structure:', {
            hasRemittance: !!responseData?.remittance,
            hasData: !!responseData?.data,
            hasId: !!responseData?.id,
            hasRemittanceNumber: !!responseData?.remittance_number,
            keys: Object.keys(responseData || {}),
            responseType: typeof responseData,
            isArray: Array.isArray(responseData),
            fullResponse: responseData,
          });
          
          // Try different structures for remittances/find
          if (responseData?.remittance) {
            return responseData.remittance as T;
          }
          if (responseData?.data?.remittance) {
            return responseData.data.remittance as T;
          }
          // If it's already the remittance object (direct return)
          if (responseData?.id || responseData?.remittance_number) {
            return responseData as T;
          }
          // If response is just the remittance data directly
          return responseData as T;
        }
        
        // Check for transceivers structure: { code: 200, data: { transceivers: [...] } } or { data: [...] }
        if (url.includes('transceivers') && !url.includes('transceivers/')) {
          // List endpoint - might return array directly or nested
          if (Array.isArray(responseData?.data)) {
            return responseData.data as T;
          }
          if (responseData?.data?.transceivers) {
            return responseData.data.transceivers as T;
          }
          if (Array.isArray(responseData)) {
            return responseData as T;
          }
        }
        
        // Check for drivers structure: { code: 200, data: { drivers: { data: [...], current_page, total, ... } } }
        if (url.includes('drivers') && !url.includes('drivers/') && !url.includes('Inquiry')) {
          // List endpoint - nested structure
          if (responseData?.data?.drivers) {
            return responseData.data.drivers as T;
          }
          // Fallback: direct array or other structure
          if (Array.isArray(responseData?.data)) {
            return responseData.data as T;
          }
        }
        
        // Check for cars structure: { code: 200, data: { cars: { data: [...], current_page, total, ... } } }
        if (url.includes('cars') && !url.includes('cars/')) {
          // List endpoint - nested structure
          if (responseData?.data?.cars) {
            return responseData.data.cars as T;
          }
          // Fallback: direct array or other structure
          if (Array.isArray(responseData?.data)) {
            return responseData.data as T;
          }
        }
        
        // Check for branches structure: { code: 200, data: { branches: { data: [...], current_page, total, ... } } }
        if (url.includes('branches') && !url.includes('branches/')) {
          // List endpoint - nested structure
          if (responseData?.data?.branches) {
            return responseData.data.branches as T;
          }
          // Fallback: direct array or other structure
          if (Array.isArray(responseData?.data)) {
            return responseData.data as T;
          }
        }
        
        // Check for branch offices structure: { code: 200, data: { offices: { data: [...], current_page, total, ... } } }
        if (url.includes('offices/branchOffices')) {
          // Branch offices endpoint - nested structure: data.offices
          if (responseData?.data?.offices) {
            return responseData.data.offices as T;
          }
          // Fallback: direct array or other structure
          if (Array.isArray(responseData?.data)) {
            return responseData.data as T;
          }
        }
        
        // Check for company users structure: { code: 200, data: { users: { data: [...], current_page, total, ... } } }
        if (url.includes('company/users') && !url.includes('company/users/')) {
          // List endpoint - nested structure: data.users
          if (responseData?.data?.users) {
            return responseData.data.users as T;
          }
          // Fallback: direct array or other structure
          if (Array.isArray(responseData?.data)) {
            return responseData.data as T;
          }
        }
        
        // Check for waybill number structure: { code: 200, data: { waybillNumbers: { data: [...], current_page, total, ... } } }
        if (url.includes('waybill_number') && !url.includes('waybill_number/')) {
          // List endpoint - nested structure: data.waybillNumbers
          if (responseData?.waybillNumbers) {
            return responseData.waybillNumbers as T;
          }
          // Fallback: check for waybill_numbers (plural)
          if (responseData?.data?.waybill_numbers) {
            return responseData.data.waybill_numbers as T;
          }
          // Check for direct paginated structure: { data: [...], current_page, total, ... }
          if (responseData?.data && Array.isArray(responseData.data)) {
            return {
              ...responseData,
              data: responseData.data,
            } as T;
          }
          // Fallback: direct array or other structure
          if (Array.isArray(responseData?.data)) {
            return responseData.data as T;
          }
        }
        
        // Check for company customers structure: { code: 200, data: { customers: { data: [...], current_page, total, ... } } }
        if (url.includes('company/customers') && !url.includes('customers/store') && !url.includes('customers/update') && !url.includes('customers/changeStatus')) {
          if (responseData?.data?.customers) {
            return responseData.data.customers as T;
          }
          if (Array.isArray(responseData?.data)) {
            return responseData.data as T;
          }
        }

        // Check for insurances structure: { code: 200, data: { contracts: { data: [...], current_page, total, ... } } }
        if (url.includes('insurances') && !url.includes('insurances/')) {
          // List endpoint - nested structure: data.contracts
          if (responseData?.data?.contracts) {
            return responseData.data.contracts as T;
          }
          // Check for insurance_companies: { code: 200, data: { insurance_companies: [...] } }
          if (responseData?.data?.insurance_companies) {
            return responseData.data.insurance_companies as T;
          }
          // Fallback: check for insurances property
          if (responseData?.data?.insurances) {
            return responseData.data.insurances as T;
          }
          // Fallback: direct array or other structure
          if (Array.isArray(responseData?.data)) {
            return responseData.data as T;
          }
        }
        
        // Check for waybills structure: { code: 200, data: { waybills: { data: [...] } } }
        if (url.includes('bill_loadings') && responseData?.data?.waybills) {
          return responseData.data.waybills as T;
        }
        // Check for waybill show endpoint: { data: { waybill: {...} } } or { data: { billLoading: {...} } }
        if (url.includes('bill_loadings/show')) {
          // The waybill data is nested in data.waybill
          if (responseData?.data?.waybill) {
            return responseData.data.waybill as T;
          }
          // Fallback: might be nested in billLoading
          if (responseData?.data?.billLoading) {
            return responseData.data.billLoading as T;
          }
          // Or it might be directly in data
          if (responseData?.data) {
            return responseData.data as T;
          }
        }
        // Check for anbarType structure: { code: 200, data: { anbarTypes: [...], selected_anbar_type } }
        if (url.includes('anbarType') && responseData?.data) {
          const inner = responseData.data as any;
          if (inner?.anbarTypes) {
            return { anbarTypes: inner.anbarTypes, selected_anbar_type: inner.selected_anbar_type ?? null } as T;
          }
        }

        // Check for nested data structure: { data: { data: {...} } }
        if ('data' in responseData && responseData.data !== undefined) {
          return responseData.data as T;
        }
        // Check for remittance structure: { data: { remittance: {...} } }
        if ('remittance' in response.data && response.data.remittance !== undefined) {
          return response.data.remittance as T;
        }
        // Return data directly
        return response.data as T;
      }
      // Check if response itself has remittance property
      if ('remittance' in response && (response as any).remittance !== undefined) {
        return (response as any).remittance as T;
      }
      return response as unknown as T;
    } catch (error: any) {
      // Log detailed error information for debugging
      console.error('API Error Details:', {
        url,
        method,
        params,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        responseData: error?.response?.data,
        message: error?.message,
        config: error?.config,
      });

      // Keep the original Axios error so callers can distinguish an
      // authentication failure from a transient timeout/gateway failure.
      // Converting every failure to Error made the app discard a valid
      // session after temporary production outages.
      if (axios.isAxiosError(error)) {
        throw error;
      }
      
      // Extract error message from various possible locations
      const message = error?.response?.data?.message || 
                     error?.response?.data?.error || 
                     error?.response?.data?.errors?.message ||
                     error?.message || 
                     'خطایی رخ داده است';
      throw new Error(message);
    }
  }

  get<T = any>(url: string, params = {}): Promise<T> {
    return this.request<T>({ url, method: 'get', params });
  }

  post<T = any>(url: string, data = {}): Promise<T> {
    return this.request<T>({ url, method: 'post', data });
  }

  patch<T = any>(url: string, data = {}): Promise<T> {
    return this.request<T>({ url, method: 'patch', data });
  }

  put<T = any>(url: string, data = {}): Promise<T> {
    return this.request<T>({ url, method: 'put', data });
  }

  delete<T = any>(url: string, data = {}): Promise<T> {
    return this.request<T>({ url, method: 'delete', data });
  }
}

export default new ApiService();
